/**
 * A model-free estimate of every bet line, built only from stored results.
 *
 * Deliberately independent of both the LLM and the market, so it can corroborate or contradict them
 * rather than echo them. Ported from `BttsPropensityModel`.
 *
 * BTTS decomposes into two near-independent questions — will the home side score, will the away
 * side score — so each is estimated by blending the attacking team's venue-specific strength with
 * the defending team's, then multiplied.
 *
 * **Poisson on goal COUNTS, not binary "did they score".** The first version blended venue-specific
 * *rates* and backtested at +3.2 points, z = 0.76 — inside noise. Goal-count strengths took it to
 * **+5.4 points, z = 1.28**. Keeping the difference between a 2.1-goal attack and a 1.1-goal one,
 * rather than collapsing both to "usually scores", is what bought that.
 *
 * **Scores rank as PERCENTILES against past fixtures, never against the same day's slate.** The 60%
 * BTTS rate is a property of the league over a season, not of any given day: a per-slate rule would
 * flag 60% of a Tuesday's two matches *and* 60% of a Saturday's fifteen even if the Tuesday pair
 * were both terrible spots.
 */
import { over25, under35, type MatchResult } from './types';

/** Pseudo-matches of league-average prior mixed into every team rate. */
export const SHRINK_K = 5;

/**
 * Flag margins are **league-relative**: a line flags when the model's probability exceeds that
 * league's own base rate by the margin.
 *
 * Absolute thresholds were a trap. "Under 3.5 ≥ 65%" was tuned on MLS (base 63%) but sat BELOW
 * Sudamericana's 78% base rate, flagging nearly everything there, while "Over ≥ 68%" could never
 * fire against its 41% base. These margins reproduce the backtested MLS cuts (68 vs 63 over,
 * 65 vs 60 under, 55 vs 47 home) and scale with each league's scoring profile.
 */
export const FLAG_MARGIN = 0.05;

/** Margin for match-result lines; larger because 1X2 markets are priced sharpest. */
export const RESULT_MARGIN = 0.08;

export interface TeamRates {
  team: string;
  homeMatches: number;
  awayMatches: number;
  homeScored: number;
  awayScored: number;
  homeConceded: number;
  awayConceded: number;
  homeGoalsFor: number;
  homeGoalsAgainst: number;
  awayGoalsFor: number;
  awayGoalsAgainst: number;
}

/** Model-estimated probability (0-1) for every supported bet line. */
export interface BetProbabilities {
  btts: number;
  bttsNo: number;
  over25: number;
  under35: number;
  homeWin: number;
  draw: number;
  awayWin: number;
}

function poisson(lambda: number, k: number): number {
  let p = Math.exp(-lambda);
  for (let i = 1; i <= k; i++) p *= lambda / i;
  return p;
}

function emptyRates(team: string): TeamRates {
  return {
    team,
    homeMatches: 0,
    awayMatches: 0,
    homeScored: 0,
    awayScored: 0,
    homeConceded: 0,
    awayConceded: 0,
    homeGoalsFor: 0,
    homeGoalsAgainst: 0,
    awayGoalsFor: 0,
    awayGoalsAgainst: 0,
  };
}

export class PropensityModel {
  private readonly teams = new Map<string, TeamRates>();
  private readonly sortedScores: number[] = [];

  matchesUsed = 0;
  leagueHomeScoredRate = 0;
  leagueAwayScoredRate = 0;
  leagueBttsRate = 0;
  leagueHomeGoalsAvg = 0;
  leagueAwayGoalsAvg = 0;
  leagueOver25Rate = 0;
  leagueUnder35Rate = 0;
  leagueHomeWinRate = 0;
  leagueDrawRate = 0;
  leagueAwayWinRate = 0;

  get teamsKnown(): number {
    return this.teams.size;
  }
  get leagueBttsNoRate(): number {
    return 1 - this.leagueBttsRate;
  }

  // League-relative flag tests. All selection logic must go through these, never absolute cuts.
  flagsOver25 = (p: number) => p >= this.leagueOver25Rate + FLAG_MARGIN;
  flagsUnder35 = (p: number) => p >= this.leagueUnder35Rate + FLAG_MARGIN;
  flagsBttsNo = (p: number) => p >= this.leagueBttsNoRate + FLAG_MARGIN;
  flagsHomeWin = (p: number) => p >= this.leagueHomeWinRate + RESULT_MARGIN;
  flagsDraw = (p: number) => p >= this.leagueDrawRate + FLAG_MARGIN;
  flagsAwayWin = (p: number) => p >= this.leagueAwayWinRate + RESULT_MARGIN;

  /** Case-insensitive, matching the C# `StringComparer.OrdinalIgnoreCase` dictionary. */
  private key = (team: string) => team.toLowerCase();

  private getOrAdd(team: string): TeamRates {
    const k = this.key(team);
    let t = this.teams.get(k);
    if (!t) {
      t = emptyRates(team);
      this.teams.set(k, t);
    }
    return t;
  }

  ratesFor(team: string): TeamRates | null {
    return this.teams.get(this.key(team)) ?? null;
  }

  /** Builds team rates from completed matches. Pass only matches played BEFORE the one being scored. */
  static build(history: readonly MatchResult[]): PropensityModel {
    const m = new PropensityModel();
    let homeScored = 0;
    let awayScored = 0;
    let btts = 0;
    let n = 0;
    let totalHomeGoals = 0;
    let totalAwayGoals = 0;
    let o25 = 0;
    let u35 = 0;
    let homeWins = 0;
    let draws = 0;
    let awayWins = 0;

    for (const r of history) {
      if (!r.homeTeam || !r.awayTeam) continue;
      n++;
      const h = m.getOrAdd(r.homeTeam);
      const a = m.getOrAdd(r.awayTeam);

      h.homeMatches++;
      a.awayMatches++;
      h.homeGoalsFor += r.homeGoals;
      h.homeGoalsAgainst += r.awayGoals;
      a.awayGoalsFor += r.awayGoals;
      a.awayGoalsAgainst += r.homeGoals;
      totalHomeGoals += r.homeGoals;
      totalAwayGoals += r.awayGoals;
      if (r.homeGoals > 0) {
        h.homeScored++;
        a.awayConceded++;
        homeScored++;
      }
      if (r.awayGoals > 0) {
        a.awayScored++;
        h.homeConceded++;
        awayScored++;
      }
      if (r.bothTeamsScored) btts++;
      if (over25(r)) o25++;
      if (under35(r)) u35++;
      if (r.homeGoals > r.awayGoals) homeWins++;
      else if (r.homeGoals === r.awayGoals) draws++;
      else awayWins++;
    }

    m.matchesUsed = n;
    if (n > 0) {
      m.leagueHomeScoredRate = homeScored / n;
      m.leagueAwayScoredRate = awayScored / n;
      m.leagueBttsRate = btts / n;
      m.leagueHomeGoalsAvg = totalHomeGoals / n;
      m.leagueAwayGoalsAvg = totalAwayGoals / n;
      m.leagueOver25Rate = o25 / n;
      m.leagueUnder35Rate = u35 / n;
      m.leagueHomeWinRate = homeWins / n;
      m.leagueDrawRate = draws / n;
      m.leagueAwayWinRate = awayWins / n;
    }

    // Score every historical fixture so a new match can be placed as a percentile against them.
    for (const r of history) {
      const s = m.propensity(r.homeTeam, r.awayTeam);
      if (s !== null) m.sortedScores.push(s);
    }
    m.sortedScores.sort((x, y) => x - y);

    return m;
  }

  /**
   * A team's goals-per-match relative to the league average (1.0 = typical), shrunk toward 1.0 by
   * {@link SHRINK_K} pseudo-matches so a thin record cannot assert an extreme strength.
   */
  private strength(goals: number | null, matches: number | null, leagueAvg: number): number {
    if (leagueAvg <= 0) return 1;
    const g = goals ?? 0;
    const n = matches ?? 0;
    return (g + SHRINK_K * leagueAvg) / (n + SHRINK_K) / leagueAvg;
  }

  /**
   * Expected goals for each side. The totals markets derive from these too — Over 2.5, Under 3.5
   * and exactly-3 are all Poisson functions of the total, so they share one estimate rather than
   * each inventing their own.
   */
  expectedGoals(homeTeam: string, awayTeam: string): { home: number; away: number } | null {
    if (this.matchesUsed === 0) return null;
    const h = this.ratesFor(homeTeam);
    const a = this.ratesFor(awayTeam);
    if (!h && !a) return null;

    const lambdaHome =
      this.leagueHomeGoalsAvg *
      this.strength(h?.homeGoalsFor ?? null, h?.homeMatches ?? null, this.leagueHomeGoalsAvg) *
      this.strength(a?.awayGoalsAgainst ?? null, a?.awayMatches ?? null, this.leagueHomeGoalsAvg);

    const lambdaAway =
      this.leagueAwayGoalsAvg *
      this.strength(a?.awayGoalsFor ?? null, a?.awayMatches ?? null, this.leagueAwayGoalsAvg) *
      this.strength(h?.homeGoalsAgainst ?? null, h?.homeMatches ?? null, this.leagueAwayGoalsAvg);

    if (lambdaHome <= 0 || lambdaAway <= 0) return null;
    return { home: lambdaHome, away: lambdaAway };
  }

  /** Estimated P(both teams score), 0-1. Null when neither side has any record. */
  propensity(homeTeam: string, awayTeam: string): number | null {
    const l = this.expectedGoals(homeTeam, awayTeam);
    if (!l) return null;
    return (1 - Math.exp(-l.home)) * (1 - Math.exp(-l.away));
  }

  /** Every bet line's probability, all from the same expected-goals estimate. */
  probabilities(homeTeam: string, awayTeam: string): BetProbabilities | null {
    const lambdas = this.expectedGoals(homeTeam, awayTeam);
    const btts = this.propensity(homeTeam, awayTeam);
    if (!lambdas || btts === null) return null;

    const t = lambdas.home + lambdas.away;

    // Match result from the joint distribution of two independent Poissons over a goal grid.
    // 10 a side is far beyond anything observed, so the tail loss is negligible.
    const MAX = 10;
    let home = 0;
    let draw = 0;
    let away = 0;
    for (let h = 0; h <= MAX; h++) {
      for (let a = 0; a <= MAX; a++) {
        const p = poisson(lambdas.home, h) * poisson(lambdas.away, a);
        if (h > a) home += p;
        else if (h === a) draw += p;
        else away += p;
      }
    }

    return {
      btts,
      bttsNo: 1 - btts,
      over25: 1 - poisson(t, 0) - poisson(t, 1) - poisson(t, 2),
      under35: poisson(t, 0) + poisson(t, 1) + poisson(t, 2) + poisson(t, 3),
      homeWin: home,
      draw,
      awayWin: away,
    };
  }

  /**
   * Where this fixture sits among historical fixtures, 0-1. Ranking against the past — rather than
   * against today's other matches — is what makes the flag rate stable across uneven slates.
   *
   * Mirrors C#'s `List.BinarySearch` + `~index`: the count of scores strictly below, except that a
   * found value returns its own index, which for duplicates is any of them. Reproduced exactly so
   * the two implementations cannot disagree on a tie.
   */
  percentileOf(homeTeam: string, awayTeam: string): number | null {
    const score = this.propensity(homeTeam, awayTeam);
    if (score === null || this.sortedScores.length === 0) return null;
    const idx = binarySearch(this.sortedScores, score);
    const below = idx >= 0 ? idx : ~idx;
    return below / this.sortedScores.length;
  }

  /**
   * True when this fixture ranks high enough to be one of the ~`leagueBttsRate` share of matches
   * expected to finish BTTS. Flagging the top 60% means the cut sits at the 40th percentile, so the
   * flag rate self-adjusts to the measured base rate rather than to a number someone chose.
   */
  isLikelyBtts(homeTeam: string, awayTeam: string): boolean {
    const pct = this.percentileOf(homeTeam, awayTeam);
    return pct !== null && pct >= 1 - this.leagueBttsRate;
  }
}

/**
 * .NET's `List<double>.BinarySearch` semantics: the index of a match, or the bitwise complement of
 * the insertion point. Ported rather than approximated because the percentile depends on which
 * index a duplicate resolves to, and "close enough" here is a parity failure.
 */
export function binarySearch(sorted: readonly number[], value: number): number {
  let lo = 0;
  let hi = sorted.length - 1;
  while (lo <= hi) {
    const mid = lo + ((hi - lo) >> 1);
    const c = sorted[mid] - value;
    if (c === 0) return mid;
    if (c < 0) lo = mid + 1;
    else hi = mid - 1;
  }
  return ~lo;
}
